import { useEffect, useState } from "react";
import { Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const STATUS_STYLE = {
    hadir: { label: <Check size={13} weight="bold" />, cls: "bg-green-100 border-green-600 text-green-700", title: "Hadir" },
    izin: { label: "I", cls: "bg-racing-gold/15 border-racing-gold text-racing-golddeep", title: "Izin" },
    sakit: { label: "S", cls: "bg-racing-gold/15 border-racing-gold text-racing-golddeep", title: "Sakit" },
    alpha: { label: <X size={13} weight="bold" />, cls: "bg-red-50 border-racing-red text-racing-red", title: "Tidak Hadir (Alpha)" },
};

export default function AttendanceMatrix() {
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
    const [data, setData] = useState(null);
    const [classFilter, setClassFilter] = useState("");
    const [loading, setLoading] = useState(false);

    const load = async (selectedMonth = month) => {
        setLoading(true);
        try {
            const { data: res } = await api.get(`/coach/attendance-matrix?month=${selectedMonth}`);
            setData(res);
        } catch {
            toast.error("Gagal memuat rekap absensi");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const changeMonth = (value) => {
        setMonth(value);
        load(value);
    };

    const classes = data ? [...new Set(data.athletes.map((a) => a.class_name || "Tanpa Kelas"))].sort() : [];
    const visible = data ? data.athletes.filter((a) => !classFilter || (a.class_name || "Tanpa Kelas") === classFilter) : [];
    const grouped = classes
        .filter((c) => !classFilter || c === classFilter)
        .map((c) => ({ name: c, athletes: visible.filter((a) => (a.class_name || "Tanpa Kelas") === c) }))
        .filter((g) => g.athletes.length > 0);

    const dates = data ? [...new Set(data.sessions.map((s) => s.date))].sort() : [];

    const exportUrl = (fmt, cls = "") =>
        `${api.defaults.baseURL}/coach/attendance-matrix/export?month=${month}&class_name=${encodeURIComponent(cls || classFilter)}&format=${fmt}`;

    return (
        <div data-testid="attendance-matrix">
            <div className="flex flex-wrap items-end gap-4 mb-6">
                <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Bulan</label>
                    <input type="month" value={month} onChange={(e) => changeMonth(e.target.value)} data-testid="attendance-matrix-month" className="bg-white border border-racing-line px-4 py-3 text-sm" />
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Kelas</label>
                    <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)} data-testid="attendance-matrix-class-filter" className="bg-white border border-racing-line px-4 py-3 text-sm">
                        <option value="">Semua Kelas</option>
                        {classes.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
                <div className="flex items-end gap-2 pb-0.5">
                    <a href={exportUrl("xlsx")} data-testid="matrix-export-xlsx-all" className="border border-racing-line px-4 py-3 text-xs uppercase tracking-[0.15em] text-racing-muted hover:text-racing-red">Excel{classFilter ? " (kelas ini)" : ""}</a>
                    <a href={exportUrl("pdf")} data-testid="matrix-export-pdf-all" className="border border-racing-line px-4 py-3 text-xs uppercase tracking-[0.15em] text-racing-muted hover:text-racing-red">PDF{classFilter ? " (kelas ini)" : ""}</a>
                </div>
                <div className="flex items-center gap-4 pb-3 text-[10px] uppercase tracking-[0.15em] text-racing-muted">
                    <span className="flex items-center gap-1.5"><span className="w-4 h-4 inline-flex items-center justify-center bg-green-100 border border-green-600 text-green-700"><Check size={10} weight="bold" /></span> Hadir</span>
                    <span className="flex items-center gap-1.5"><span className="w-4 h-4 inline-flex items-center justify-center bg-racing-gold/15 border border-racing-gold text-racing-golddeep text-[9px]">I/S</span> Izin/Sakit</span>
                    <span className="flex items-center gap-1.5"><span className="w-4 h-4 inline-flex items-center justify-center bg-red-50 border border-racing-red text-racing-red"><X size={10} weight="bold" /></span> Alpha</span>
                </div>
            </div>

            {loading && <p className="text-racing-muted">Memuat rekap...</p>}
            {!loading && data && dates.length === 0 && <p className="border border-racing-line bg-white p-6 text-sm text-racing-muted" data-testid="attendance-matrix-empty">Belum ada sesi absensi pada bulan ini.</p>}

            {!loading && data && dates.length > 0 && grouped.map((group, gi) => (
                <div key={group.name} className="mb-8" data-testid={`attendance-matrix-class-${gi}`}>
                    <div className="flex items-center gap-3 mb-3">
                        <p className="text-xs uppercase tracking-[0.25em] text-racing-golddeep">{group.name} — {group.athletes.length} atlet</p>
                        <a href={exportUrl("xlsx", group.name)} data-testid={`matrix-export-xlsx-${gi}`} className="text-[10px] uppercase tracking-[0.15em] text-racing-muted hover:text-racing-red border border-racing-line px-2 py-1">Excel</a>
                        <a href={exportUrl("pdf", group.name)} data-testid={`matrix-export-pdf-${gi}`} className="text-[10px] uppercase tracking-[0.15em] text-racing-muted hover:text-racing-red border border-racing-line px-2 py-1">PDF</a>
                    </div>
                    <div className="border border-racing-line overflow-x-auto bg-white">
                        <table className="text-sm">
                            <thead>
                                <tr className="border-b border-racing-line bg-racing-surface">
                                    <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-racing-muted sticky left-0 bg-racing-surface min-w-[200px]">Atlet</th>
                                    {dates.map((d) => (
                                        <th key={d} className="px-2 py-3 text-[10px] uppercase tracking-[0.1em] text-racing-muted text-center min-w-[44px]" title={d}>{d.slice(8)}</th>
                                    ))}
                                    <th className="px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-racing-red text-center min-w-[70px]">Absen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {group.athletes.map((athlete, ai) => {
                                    const absences = dates.filter((d) => ["izin", "sakit", "alpha"].includes(athlete.days[d])).length;
                                    return (
                                        <tr key={athlete.id} className="border-b border-racing-line last:border-0 hover:bg-black/[0.02]" data-testid={`attendance-matrix-row-${gi}-${ai}`}>
                                            <td className="px-4 py-2.5 sticky left-0 bg-white font-medium text-neutral-900 text-xs md:text-sm">{athlete.name}</td>
                                            {dates.map((d, di) => {
                                                const status = athlete.days[d];
                                                const style = STATUS_STYLE[status];
                                                return (
                                                    <td key={d} className="px-2 py-2.5 text-center" data-testid={`attendance-matrix-cell-${gi}-${ai}-${di}`}>
                                                        {style ? (
                                                            <span className={`inline-flex items-center justify-center w-6 h-6 border text-[10px] font-semibold ${style.cls}`} title={`${d}: ${style.title}`}>{style.label}</span>
                                                        ) : (
                                                            <span className="inline-flex items-center justify-center w-6 h-6 text-racing-muted/50" title={`${d}: tidak ada catatan`}>–</span>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                            <td className="px-3 py-2.5 text-center">
                                                <span className={`font-display text-lg ${absences > 0 ? "text-racing-red" : "text-racing-muted/50"}`} data-testid={`attendance-matrix-absen-${gi}-${ai}`}>{absences}</span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}
        </div>
    );
}
